import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  CreateGeographicAreaPayload,
  FinanceApiService,
  GeographicAreaRecord,
  UpdateGeographicAreaPayload
} from '../finance/finance-api.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';

type LevelOption = { value: string; labelKey: string };

@Component({
  standalone: true,
  selector: 'ain-platform-geography-area-detail',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, SelectModule, TableModule],
  template: `
    <ain-page [title]="isCreateMode() ? t('platform.geography.form.createTitle') : t('platform.geography.form.editTitle')" [description]="t('platform.geography.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card header">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/platform/geography']">{{ t('catalog.action.backToList') }}</a>
            <div class="actions">
              @if (!isCreateMode()) {
                <button pButton type="button" severity="secondary" [label]="t('platform.geography.action.addChild')" (click)="openCreateChild()"></button>
              }
              <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
            </div>
          </div>
        </header>

        @if (isCreateMode()) {
          <section class="card grid">
            <label>
              <span>{{ t('sales.territories.fields.level') }}</span>
              <p-select [options]="levelOptions" optionValue="value" [(ngModel)]="form.level" appendTo="body" (ngModelChange)="onLevelChanged()">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            @if (form.level !== 'Country') {
              <label>
                <span>{{ t('sales.territories.fields.parent') }}</span>
                <p-select [options]="parentOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.parentAreaId" [showClear]="true" appendTo="body" />
              </label>
            }
            <label><span>{{ t('sales.territories.fields.code') }}</span><input pInputText [(ngModel)]="form.code" /></label>
            <label><span>{{ t('sales.territories.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
          </section>
        } @else {
          <section class="card grid">
            <label><span>{{ t('sales.territories.fields.code') }}</span><input pInputText [(ngModel)]="form.code" readonly /></label>
            <label><span>{{ t('sales.territories.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
            <label class="check"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ t('sales.territories.active') }}</span></label>
          </section>
          <section class="card">
            <h3>{{ t('platform.geography.tabs.children') }}</h3>
            <p-table [value]="children()" [paginator]="true" [rows]="10">
              <ng-template pTemplate="header">
                <tr><th>{{ t('sales.territories.fields.code') }}</th><th>{{ t('sales.territories.fields.name') }}</th><th>{{ t('sales.territories.fields.level') }}</th><th>{{ t('columns.status') }}</th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.code }}</td>
                  <td><a [routerLink]="['/platform/geography', row.id]" class="link">{{ row.name }}</a></td>
                  <td>{{ levelLabel(row.level) }}</td>
                  <td>{{ row.isActive ? t('sales.territories.active') : t('sales.territories.inactive') }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .actions { display: flex; gap: 8px; }
    .back-link, .link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 10px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    h3 { margin: 0 0 8px; font-size: 15px; }
    .error { color: #b91c1c; }
  `]
})
export class PlatformGeographyAreaDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly children = signal<GeographicAreaRecord[]>([]);
  readonly parentCandidates = signal<GeographicAreaRecord[]>([]);

  form = { code: '', name: '', level: 'Country', parentAreaId: null as string | null, isActive: true };

  readonly levelOptions: LevelOption[] = [
    { value: 'Country', labelKey: 'platform.geography.levels.country' },
    { value: 'Province', labelKey: 'platform.geography.levels.province' },
    { value: 'City', labelKey: 'platform.geography.levels.city' },
    { value: 'Area', labelKey: 'platform.geography.levels.area' },
    { value: 'Route', labelKey: 'platform.geography.levels.route' }
  ];

  private areaId = '';

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('areaId');
    if (id === 'create') {
      const level = this.route.snapshot.queryParamMap.get('level') ?? 'Country';
      const parentAreaId = this.route.snapshot.queryParamMap.get('parentAreaId');
      this.form.level = level;
      if (parentAreaId) this.form.parentAreaId = parentAreaId;
      this.loading.set(false);
      this.onLevelChanged();
      return;
    }

    this.areaId = id ?? '';
    if (!this.areaId) {
      this.loading.set(false);
      return;
    }

    this.api.getGeographicArea(this.areaId).subscribe({
      next: area => {
        this.form = { code: area.code, name: area.name, level: area.level, parentAreaId: area.parentAreaId ?? null, isActive: area.isActive };
        this.loadChildren();
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  isCreateMode(): boolean {
    return this.route.snapshot.paramMap.get('areaId') === 'create';
  }

  parentOptions(): { label: string; value: string }[] {
    return this.parentCandidates().map(item => ({ label: `${item.code} — ${item.name}`, value: item.id }));
  }

  onLevelChanged(): void {
    const parentLevel = this.parentLevelFor(this.form.level);
    if (!parentLevel) {
      this.parentCandidates.set([]);
      this.form.parentAreaId = null;
      return;
    }
    this.api.listGeographicAreas({ level: parentLevel }).subscribe({
      next: rows => this.parentCandidates.set(rows)
    });
  }

  loadChildren(): void {
    this.api.listGeographicAreas({ parentAreaId: this.areaId }).subscribe({
      next: rows => this.children.set(rows)
    });
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');
    if (this.isCreateMode()) {
      const payload: CreateGeographicAreaPayload = {
        parentAreaId: this.form.parentAreaId,
        code: this.form.code,
        name: this.form.name,
        level: this.form.level
      };
      this.api.createGeographicArea(payload).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/platform/geography', created.id]);
        },
        error: () => {
          this.saving.set(false);
          this.errorMessage.set(this.t('common.error'));
        }
      });
      return;
    }

    const payload: UpdateGeographicAreaPayload = { name: this.form.name, isActive: this.form.isActive };
    this.api.updateGeographicArea(this.areaId, payload).subscribe({
      next: () => this.saving.set(false),
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('common.error'));
      }
    });
  }

  openCreateChild(): void {
    const childLevel = this.childLevelFor(this.form.level);
    if (!childLevel) return;
    void this.router.navigate(['/platform/geography/create'], {
      queryParams: { level: childLevel, parentAreaId: this.areaId }
    });
  }

  levelLabel(level: string): string {
    return this.t(`platform.geography.levels.${level.toLowerCase()}`);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private parentLevelFor(level: string): string | null {
    const map: Record<string, string | null> = {
      Country: null,
      Province: 'Country',
      City: 'Province',
      Area: 'City',
      Route: 'Area'
    };
    return map[level] ?? null;
  }

  private childLevelFor(level: string): string | null {
    const map: Record<string, string | null> = {
      Country: 'Province',
      Province: 'City',
      City: 'Area',
      Area: 'Route',
      Route: null
    };
    return map[level] ?? null;
  }
}
